// The model document's selection — the live Selection instance, its history
// serialization, and the "floating selection" (selFloat) protocol. Owned here
// (core/edit-core) so tools/slices import one API instead of sharing main-scoped
// `let`s; main and the gesture handlers are the current consumers.
//
// Layering: this module sits on model/ only. The one selFloat consumer that
// touches gesture state (adoptSelFloat) stays with the gesture code — it reads
// this API rather than the other way around.

import { Selection } from "../../model/selection";
import { type PaletteIndex } from "../../model/voxel-grid";
import {
  beginTransaction,
  endTransaction,
  onSelectionHistory,
} from "../../model/history";
import { grid } from "./grid";

export const selection = new Selection();

// Canonical string of the selection (sorted cell keys) so history can store and
// compare selection states by value, and restore them on undo/redo.
export function selectionStateString(): string {
  const keys: string[] = [];
  for (const c of selection.entries()) keys.push(`${c.ix},${c.iy},${c.iz}`);
  keys.sort();
  return keys.join(";");
}
function parseSelectionState(state: string): { ix: number; iy: number; iz: number }[] {
  if (!state) return [];
  return state.split(";").map((k) => {
    const p = k.split(",");
    return { ix: Number(p[0]), iy: Number(p[1]), iz: Number(p[2]) };
  });
}
// The selection as a Set of "ix,iy,iz" keys — used to mask cells out of an
// extrude (empty when nothing is selected).
export function selectionKeySet(): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const c of selection.entries()) keys.add(`${c.ix},${c.iy},${c.iz}`);
  return keys;
}
// Make selecting an undoable action: every transaction records the selection
// before/after, and undo/redo restore it (see history.ts).
onSelectionHistory(selectionStateString, (state) =>
  selection.replace(parseSelectionState(state)),
);

// Delete every selected voxel and drop the selection — one undo step (the
// transaction records the selection change too, via the wiring above).
export function deleteSelection(): void {
  if (selection.size() === 0) return;
  beginTransaction();
  for (const c of selection.entries()) {
    grid.delete(c.ix, c.iy, c.iz);
  }
  selection.replace([]);
  endTransaction();
}

// ── Floating selection (selFloat) ───────────────────────────────────────────
// The cells the current selection is sitting ON TOP of, stashed (with their
// original palettes) so they can be restored. It makes selections
// NON-DESTRUCTIVE — moving the selection over existing voxels covers (not
// destroys) them, and a later move restores them. Set by:
//   • duplicate/paste (the copy covers whatever it lands on), and
//   • every move (the selection now covers the cells under its new position).
// `selVersion` tracks the selection it belongs to — bumped through add/subtract
// so the float survives them; `gridVersion` invalidates it on any direct grid
// edit. Cleared (= committed where it sits) on deselect / a fresh replace
// selection / mode switch.
export interface SelFloat {
  displaced: Map<string, PaletteIndex>;
  selVersion: number;
  gridVersion: number;
}
let selFloat: SelFloat | null = null;

export function getSelFloat(): SelFloat | null {
  return selFloat;
}
// Stash `displaced` as the float for the CURRENT selection + grid state — call
// at the moment the cover happens (versions are captured here).
export function setSelFloat(displaced: Map<string, PaletteIndex>): void {
  selFloat = { displaced, selVersion: selection.version, gridVersion: grid.version };
}
export function clearSelFloat(): void {
  selFloat = null;
}
// True while the float still belongs to the live selection and untouched grid.
export function selFloatValid(): boolean {
  return (
    selFloat !== null &&
    selection.version === selFloat.selVersion &&
    grid.version === selFloat.gridVersion
  );
}
